<script>
import {useEvaluationFileStore} from "@/stores/Evaluation/EvaluationFile.js"
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {ref, watch} from "vue";
import {useSecurityStore} from "@/stores/Security.js";
import axios from "axios";
import EvaluationFileListingDataTable from "@/views/Evaluation/File/Components/EvaluationFileListingDataTable.vue";
import EvaluationFileDataTable from "@/views/Evaluation/File/Components/EvaluationFileDataTable.vue";

export default {
    components: {EvaluationFileDataTable, EvaluationFileListingDataTable},
    props: {
        type: String,
        rootPath: String,
        intranet: Boolean,
    },
    setup(props) {
        const {loading, filter, backgroundColor} = useSharedData();

        const {transformFilterValues} = useSharedMethod();

        const {$router} = useTools();

        const evaluationFileStore = useEvaluationFileStore();
        const securityStore = useSecurityStore();

        const search = ref(null);

        filter.value = {
            archive: false,
            indexId: null,
        };

        function findAll() {
            loading.value = true;
            filter.value.type = props.type;
            axios.get('/evaluationFiles', {params: transformFilterValues(filter.value)}).then((response) => {
                evaluationFileStore.setEvaluationFiles(response.data.fileElements);
                evaluationFileStore.setEvaluationListing(response.data.listingElements);
            }).finally(() => {
                loading.value = false;
            });
        }

        function updateCriterion({item}) {
            axios.put(`/evaluationFileCriteria`, {
                id: item.id,
                measureName: item.measureName,
                measureKeyPerformanceIndicator: item.measureKeyPerformanceIndicator,
                measureNotes: item.measureNotes,
                target: item.target,
                gravity: item.gravity,
                deadline: item.deadline,
            }).then(response => {
                evaluationFileStore.updateEvaluationFile(response.data);
            }).finally(() => {

            });
        }

        function addCriterion(item) {
            if (!item.measureName) return false;

            // The file is only read, to compute the next order. It is not modified.
            const file = (evaluationFileStore.evaluationFiles ?? []).find(f => f.code === item.fileCode);
            const category = file?.categories?.find(c => c.id === item.categoryId) ?? file?.categories?.[0];

            const fileOrders = (category?.criteria ?? []).map(c => c.order ?? -1);
            const listingOrders = (evaluationFileStore.evaluationListing ?? [])
                .filter(r => r.fileCode === item.fileCode)
                .map(r => r.order ?? -1);

            // next order: highest existing + 1 (0 when there is none)
            const order = Math.max(-1, ...fileOrders, ...listingOrders) + 1;

            axios.post(`/evaluationFileCriteria`, {
                // no `id`: this is a new criterion
                categoryId: item.categoryId,
                categoryName: item.categoryName,
                measureName: item.measureName,
                measureKeyPerformanceIndicator: item.measureKeyPerformanceIndicator,
                measureNotes: item.measureNotes,
                target: item.target,
                gravity: item.gravity,
                deadline: item.deadline,
                order: order,
                type: props.type,
            }).then((response) => {
                findAll();
                /*const created = response.data;

                if (!created || created.id == null) {
                    // backend did not return the created criterion: fall back to a reload
                    findAll();
                    return;
                }

                // listing only: insert after the last row of the same group (`code`)
                const list = evaluationFileStore.evaluationListing;
                let last = -1;
                list.forEach((r, i) => {
                    if (r.code === item.code) last = i;
                });

                // copy the group's attributes, minus the synthetic-row flags
                const base = {...item};
                delete base.isNew;
                delete base.sourceId;

                list.splice(last === -1 ? list.length : last + 1, 0, {
                    ...base,                                      // keeps code, name, fileCode, ... of the group
                    id: created.id,                               // only the criterion's own fields come from the response
                    order: created.order ?? order,
                    measureName: created.measureName ?? item.measureName,
                    measureKeyPerformanceIndicator: created.measureKeyPerformanceIndicator ?? item.measureKeyPerformanceIndicator,
                    measureNotes: created.measureNotes ?? item.measureNotes,
                    target: created.target ?? item.target,
                    gravity: created.gravity ?? item.gravity,
                    deadline: created.deadline ?? item.deadline,
                });*/
            });

            return true;
        }

        function view(item) {
            $router.push({name: `${props.rootPath}.view`, params: {id: item.id}});
        }

        watch(() => filter.value, () => {
            findAll();
        }, {deep: true, immediate: false});


        return {
            evaluationFileStore,
            securityStore,
            loading,
            filter,
            backgroundColor,
            search,
            view,
            findAll,
            updateCriterion,
            addCriterion,
        };
    },
};
</script>

<template>
    <div>
        <w-app
            :title="$t(`module.${rootPath}`)"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="!intranet && type !== 'EVALUATION_PERFORMANCE'"
                    @press="$router.push({ name: `${rootPath}.add` })"
                />
                <w-btn-add
                    :show="!intranet && type === 'EVALUATION_PERFORMANCE'"
                    @press="$router.push({ name: `evaluation_performance_goal_file_indices.add` })"
                />
                <w-btn
                    v-if="!intranet"
                    icon="mdi-plus-circle-multiple"
                    color="teal"
                    :text="$t('common.multiple_addition')"
                    @press="$router.push({ name: `${rootPath}.bulk_update` })"
                />
            </template>

            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="3">
                            <w-text-field
                                v-model="search"
                                :label="$t('common.name')"
                            />
                        </v-col>
                        <v-col cols="3">
                            <w-enum-select
                                v-model="filter.archive"
                                :items="[
                                    {id: false, name: $t('common.in_progress')},
                                    {id: true, name: $t('common.archive')}
                                ]"
                                :label="$t('common.state')"
                            />
                        </v-col>
                        <v-col cols="3"></v-col>
                        <v-col cols="3" v-if="type === 'EVALUATION_PERFORMANCE'">
                            <str-evaluation-file-index-autocomplete
                                v-model="filter.indexId"
                                not-clearable
                                max-select
                                :label="$t('evaluation.cycle')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <v-tabs
                    v-if="type === 'EVALUATION_PERFORMANCE'"
                    class="tabs"
                    color="teal"
                    :background-color="backgroundColor"
                    slider-size="5"
                >

                    <v-tab
                        v-for="(category, index) in evaluationFileStore.evaluationListing"
                        :key="`tab-${index}-${category.name}`"
                    >
                        {{ $t(`${category.name} (${category.gravity}%)`) }}
                    </v-tab>
                    <v-tab>
                        {{ $t(`evaluation.file_display_mode_FILE`) }}
                    </v-tab>

                    <v-tab-item
                        v-for="(category, index) in evaluationFileStore.evaluationListing"
                        :key="`tab-item-${index}-${category.name}`"
                    >
                        <evaluation-file-listing-data-table
                            identity="code"
                            :items="category.criteria"
                            :loading="loading"
                            :readonly="category.distribution === 'COMMON'"
                            :common="category.distribution === 'COMMON'"
                            @add="addCriterion"
                            @update="updateCriterion"
                            type="EVALUATION_PERFORMANCE"
                        />

                    </v-tab-item>
                    <v-tab-item>
                        <evaluation-file-data-table
                            identity="id"
                            :items="evaluationFileStore.evaluationFiles"
                            :search="search"
                            :loading="loading"
                            :root-path="rootPath"
                            :intranet="intranet"
                            @view="view"
                        />
                    </v-tab-item>

                </v-tabs>
                <evaluation-file-data-table
                    v-else
                    identity="id"
                    :items="evaluationFileStore.evaluationFiles"
                    :search="search"
                    :loading="loading"
                    :root-path="rootPath"
                    :intranet="intranet"
                    @view="view"
                />
            </template>
        </w-app>
        <router-view/>
    </div>
</template>
